/* eslint-disable max-len */
import { useState, useEffect, useMemo } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import { useEntryType } from './useEntryType';
import {
  getMetadataAttributeData,
  getProductSpecificationData,
  getSearchModelFilterData,
  buildHeader
} from '../utils';
import { IMAGES } from '../constants';

const { string, bool, shape, arrayOf } = proptypes;

export const useApplianceFuelType = ({
  itemId,
  drawerStatus,
  product,
  metadata,
  search,
  entryType,
  allowHeader,
  imageCacheEnabled,
  skipEntryTypeCheck
}) => {
  const hasCategory = (drawerStatus?.isDryer || drawerStatus?.isRange);
  const hasType = drawerStatus?.hasElectric
    || drawerStatus?.hasGas
    || drawerStatus?.hasInduction
    || drawerStatus?.hasDualFuel
    || drawerStatus?.hasHeatpump;
  const hasDrawerStatus = hasCategory && hasType;
  const hasProductData = !!product;
  const hasMetadataData = !!metadata;
  const hasSearchData = !!search;

  const [isCaching, setIsCaching] = useState(imageCacheEnabled && typeof window !== 'undefined');
  const resolvedEntryType = useEntryType({ entryType, skipEntryTypeCheck });

  /* istanbul ignore next */
  useEffect(() => {
    const cacheImages = async () => {
      try {
        if (!resolvedEntryType) throw new Error('No entryType provided to ApplianceFuelType');
        await Promise.all(Object.values(IMAGES).map(async (src) => {
          return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = src;
            image.onload = resolve;
            image.onerror = reject;
          });
        }));
        setIsCaching(false);
      } catch (er) {
        console.log(er);
        setIsCaching(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, [imageCacheEnabled, resolvedEntryType]);

  const hasExistingDataForProduct = hasDrawerStatus || hasProductData || hasSearchData;
  const {
    data: productData,
    loading: productLoading,
    error: productError
  } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId || hasExistingDataForProduct
  });

  const finalProductData = productData?.product ?? product;
  const { isSuperSku, parentId } = finalProductData?.identifiers || {};

  const hasExistingDataForMetadata = hasDrawerStatus || hasMetadataData || hasSearchData;
  const {
    data: metadataData,
    loading: metadataLoading,
    error: metadataError
  } = useDataModel('metadata', {
    variables: {
      parentId
    },
    skip: !parentId || !isSuperSku || productLoading || hasExistingDataForMetadata
  });

  const finalMetadataData = metadataData?.metadata ?? metadata;

  const { productTypes = [], productCategories = [], fuelType = '' } = !(productLoading || hasSearchData) ? getProductSpecificationData(finalProductData) : {};
  const { metadataTypes = [], metadataCategories = [], selectedType = '' } = !(productLoading || metadataLoading || hasSearchData) ? getMetadataAttributeData(finalMetadataData) : {};
  const { filterTypes = [], filterCategories = [] } = hasSearchData ? getSearchModelFilterData(search) : {};

  const isLoading = useMemo(() => isCaching || productLoading || metadataLoading, [isCaching, productLoading, metadataLoading]);
  const error = useMemo(() => !!productError || !!metadataError, [productError, metadataError]);

  if (error) {
    console.error({ productError, metadataError });
    throw new Error('Appliance Fuel Type Error');
  }

  const {
    types = [fuelType, ...productTypes, ...metadataTypes, ...filterTypes].filter((type) => !!type),
    categories = [...productCategories, ...metadataCategories, ...filterCategories].filter((category) => !!category),
    isDryer = (categories?.includes('dryer') || categories?.includes('dryers')),
    isRange = (categories?.includes('range') || categories?.includes('ranges')),
    hasElectric = types?.includes('electric'),
    hasGas = types?.includes('gas'),
    hasInduction = types?.includes('induction'),
    hasDualFuel = types?.includes('dual fuel'),
    hasHeatpump = types?.includes('heatpump')
  } = drawerStatus || {};

  const {
    shouldShowHeader,
    headerName,
    headerLabel
  } = buildHeader({
    allowHeader,
    isDryer,
    metadataTypes,
    fuelType,
    selectedType
  });

  const dataCheck = (itemId || product || search);
  const loadCheck = (isLoading || !error);
  const isValidAppliance = dataCheck && loadCheck && !!categories?.length;
  const fuelTypeWillRender = useMemo(() => hasDrawerStatus
    || (isValidAppliance && resolvedEntryType),
  [hasDrawerStatus, isValidAppliance, resolvedEntryType]);

  return {
    drawerStatus: {
      types,
      categories,
      isDryer,
      isRange,
      hasElectric,
      hasGas,
      hasInduction,
      hasDualFuel,
      hasHeatpump
    },
    shouldShowHeader,
    headerName,
    headerLabel,
    isLoading,
    error,
    fuelTypeWillRender
  };
};

useApplianceFuelType.propTypes = {
  itemId: string.isRequired,
  entryType: string.isRequired,
  allowHeader: bool.isRequired,
  imageCacheEnabled: bool.isRequired,
  skipEntryTypeCheck: bool,
  drawerStatus: shape({
    types: arrayOf(string).isRequired,
    categories: arrayOf(string).isRequired,
    isDryer: bool,
    isRange: bool,
    hasElectric: bool,
    hasGas: bool,
    hasInduction: bool,
    hasDualFuel: bool,
    hasHeatpump: bool
  }).isRequired,
  product: shape({
    itemId: string,
    dataSource: string,
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

useApplianceFuelType.defaultProps = {
  skipEntryTypeCheck: false,
  product: null,
  metadata: null,
  search: null
};
